import { carBrands } from "../../data/carBrands";
import modelosJson from "../../data/dades/modelos.json";
import matriculasJson from "../../data/ultimes_matricules.json";

/**
 * El model relacional de /dades, en una sola font: les taules amb els seus camps (per al model
 * lògic, el físic i el CREATE TABLE) i les files ja normalitzades (per a SQLite i els gràfics).
 * Els noms van en castellà, com als exercicis de l'assignatura.
 */

// ─── Esquema ──────────────────────────────────────────────────────────────────

export interface Columna {
  nom: string;
  /** Tipus a MySQL / MariaDB. */
  tipus: string;
  pk?: boolean;
  fk?: string;
  nul?: boolean;
  extra?: string;
  /** Per què aquest tipus. */
  why: string;
}

export interface Taula {
  nom: string;
  /** Taula pont d'una relació N:M. */
  pont?: boolean;
  desc: string;
  cols: Columna[];
}

export const TAULES: Taula[] = [
  {
    nom: "Marcas",
    desc: "Una fila per marca.",
    cols: [
      { nom: "MarcaID", tipus: "VARCHAR(40)", pk: true, why: "És el slug de la web (alfa-romeo): ja és únic i surt a l'adreça de cada marca." },
      { nom: "Nombre", tipus: "VARCHAR(60)", extra: "NOT NULL UNIQUE", why: "No hi pot haver dues marques amb el mateix nom." },
      { nom: "Descripcion", tipus: "VARCHAR(255)", nul: true, why: "Text curt; pot faltar." },
    ],
  },
  {
    nom: "Combustibles",
    desc: "Cada tipus de motor tal com el posa la revista, i la família a què pertany.",
    cols: [
      { nom: "CombustibleID", tipus: "INT", pk: true, extra: "AUTO_INCREMENT", why: "Clau artificial: el nom és llarg i podria canviar." },
      { nom: "Nombre", tipus: "VARCHAR(60)", extra: "NOT NULL UNIQUE", why: "«Gasolina MHEV», «PHEV»…" },
      { nom: "Grupo", tipus: "VARCHAR(20)", extra: "NOT NULL CHECK (Grupo IN ('Elèctric','Electrificat','Combustió'))", why: "Només tres valors possibles: el CHECK ho garanteix." },
    ],
  },
  {
    nom: "Modelos",
    desc: "Una fila per model i combustible (la revista llista cada versió a part).",
    cols: [
      { nom: "ModeloID", tipus: "INT", pk: true, extra: "AUTO_INCREMENT", why: "El nom sol no identifica: «Karoq» surt amb gasolina i amb dièsel." },
      { nom: "Nombre", tipus: "VARCHAR(80)", extra: "NOT NULL", why: "«500e Coupé»." },
      { nom: "Potencia", tipus: "SMALLINT", nul: true, why: "CV: com a molt uns pocs milers, i SMALLINT arriba a 32.767." },
      { nom: "Longitud", tipus: "SMALLINT", nul: true, why: "Mil·límetres, sense decimals." },
      { nom: "Anchura", tipus: "SMALLINT", nul: true, why: "Mil·límetres." },
      { nom: "Altura", tipus: "SMALLINT", nul: true, why: "Mil·límetres." },
      { nom: "Maletero", tipus: "SMALLINT", nul: true, why: "Litres." },
      { nom: "MarcaID", tipus: "VARCHAR(40)", fk: "Marcas", extra: "NOT NULL", why: "Costat N de MARCA fabrica MODELO." },
      { nom: "CombustibleID", tipus: "INT", fk: "Combustibles", extra: "NOT NULL", why: "Costat N de MODELO usa COMBUSTIBLE." },
    ],
  },
  {
    nom: "Ediciones",
    desc: "Cada número de la revista d'on surten els preus.",
    cols: [
      { nom: "EdicionID", tipus: "INT", pk: true, extra: "AUTO_INCREMENT", why: "Clau artificial." },
      { nom: "Revista", tipus: "VARCHAR(60)", extra: "NOT NULL", why: "«Coches TV»." },
      { nom: "Fecha", tipus: "DATE", extra: "NOT NULL", why: "El mes de l'edició (el dia 1). Amb DATE es pot ordenar i comparar." },
      { nom: "Paginas", tipus: "VARCHAR(20)", nul: true, why: "Text («91–97»): no s'hi fan càlculs." },
    ],
  },
  {
    nom: "Precios",
    pont: true,
    desc: "Taula pont de MODELO aparece EDICION: el preu de cada model a cada edició.",
    cols: [
      { nom: "ModeloID", tipus: "INT", pk: true, fk: "Modelos", why: "Part de la PK composta." },
      { nom: "EdicionID", tipus: "INT", pk: true, fk: "Ediciones", why: "Part de la PK composta." },
      { nom: "Precio", tipus: "DECIMAL(10,2)", extra: "NOT NULL CHECK (Precio > 0)", why: "Diners: DECIMAL i no FLOAT, que arrodoneix. Depèn de la parella model–edició." },
    ],
  },
  {
    nom: "Matriculaciones",
    desc: "Matrícules vistes al carrer.",
    cols: [
      { nom: "MatriculacionID", tipus: "INT", pk: true, extra: "AUTO_INCREMENT", why: "Clau artificial: la matrícula surt mig amagada (76** NJJ) i es pot repetir." },
      { nom: "Fecha", tipus: "DATE", extra: "NOT NULL", why: "Ve com a 19/12/2025 i es guarda com a 2025-12-19." },
      { nom: "Matricula", tipus: "VARCHAR(10)", extra: "NOT NULL", why: "Text amb lletres i espais." },
      { nom: "Modelo", tipus: "VARCHAR(60)", nul: true, why: "Text i no FK: no diu la versió ni el combustible, i sovint és un model vell o una moto." },
      { nom: "MarcaID", tipus: "VARCHAR(40)", fk: "Marcas", nul: true, why: "Pot ser NULL: motos i camions (Kymco, DAF…) no són al catàleg." },
    ],
  },
  {
    nom: "Descatalogados",
    desc: "Models que ja no es fabriquen.",
    cols: [
      { nom: "DescatalogadoID", tipus: "INT", pk: true, extra: "AUTO_INCREMENT", why: "Clau artificial." },
      { nom: "Nombre", tipus: "VARCHAR(80)", extra: "NOT NULL", why: "«147», «Giulietta»." },
      { nom: "Combustible", tipus: "VARCHAR(60)", nul: true, why: "Text lliure («Gasolina / Dièsel»): són combinacions, no un sol combustible." },
      { nom: "Generaciones", tipus: "TINYINT", nul: true, why: "Un número petit." },
      { nom: "AnioInicio", tipus: "SMALLINT", nul: true, why: "La revista diu «2000–2009»: dos valors en una cel·la. Separats, compleixen l'atomicitat." },
      { nom: "AnioFin", tipus: "SMALLINT", nul: true, why: "Igual. Es pot calcular quants anys es va fer." },
      { nom: "MarcaID", tipus: "VARCHAR(40)", fk: "Marcas", extra: "NOT NULL", why: "Costat N de MARCA tuvo DESCATALOGADO." },
    ],
  },
];

/**
 * CREATE TABLE per a MySQL 8 / MariaDB (el que porta Local, localwp.com), en ordre perquè cada
 * FK apunti a una taula que ja existeix. InnoDB perquè les FK es comprovin i utf8mb4 pels accents.
 */
export function ddl(dialecte: "mysql" | "sqlite" = "mysql"): string {
  return TAULES.map((t) => {
    const w = Math.max(...t.cols.map((c) => c.nom.length));
    const pks = t.cols.filter((c) => c.pk);
    const lines = t.cols.map((c) => {
      let extra = c.extra ?? "";
      if (dialecte === "sqlite") extra = extra.replace("AUTO_INCREMENT", "").trim();
      const pk = pks.length === 1 && c.pk ? " PRIMARY KEY" : "";
      const nn = !c.nul && !c.pk && !extra.includes("NOT NULL") ? " NOT NULL" : "";
      return `  ${c.nom.padEnd(w)} ${c.tipus}${pk}${extra ? " " + extra : ""}${nn}`;
    });
    if (pks.length > 1) lines.push(`  PRIMARY KEY (${pks.map((c) => c.nom).join(", ")})`);
    // Les FK es diuen igual que la PK a què apunten.
    for (const c of t.cols.filter((c) => c.fk)) lines.push(`  FOREIGN KEY (${c.nom}) REFERENCES ${c.fk}(${c.nom})`);
    const engine = dialecte === "mysql" ? " ENGINE=InnoDB DEFAULT CHARSET=utf8mb4" : "";
    return `CREATE TABLE ${t.nom} (\n${lines.join(",\n")}\n)${engine};`;
  }).join("\n\n");
}

// ─── Files ────────────────────────────────────────────────────────────────────

type ModeloRaw = {
  marca: string;
  model: string;
  combustible: string;
  potencia: number | null;
  longitud: number | null;
  amplada: number | null;
  alcada: number | null;
  maleter: number | null;
  preu_marc: number | null;
  preu_juliol: number | null;
  estat: string;
};
type MatriculaRaw = { date: string; plate: string; brand: string; model: string };

export type Grupo = "Elèctric" | "Electrificat" | "Combustió";
export const GRUPOS: Grupo[] = ["Elèctric", "Electrificat", "Combustió"];

/** Els noms de combustible de la revista, en tres famílies. */
export function grupo(fuel: string): Grupo {
  const f = fuel.toLowerCase();
  if (/phev|hev|híbrid|hybrid|endollable|mhev/.test(f)) return "Electrificat";
  if (/elèctric|electric/.test(f) && !/gasolina|dièsel/.test(f)) return "Elèctric";
  return "Combustió";
}

/** "Mercedes-Benz", "MERCEDES BENZ" i "mercedes-benz" són la mateixa marca; "Škoda" i "SKODA" també. */
const clau = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "");
const slug = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/** La revista separa algunes submarques; la web les té dins de la marca. */
const ALIES: Record<string, string> = { defender: "landrover", discovery: "landrover", rangerover: "landrover" };

export interface Marca { MarcaID: string; Nombre: string; Descripcion: string | null }
export interface Combustible { CombustibleID: number; Nombre: string; Grupo: Grupo }
export interface Modelo { ModeloID: number; Nombre: string; Potencia: number | null; Longitud: number | null; Anchura: number | null; Altura: number | null; Maletero: number | null; MarcaID: string; CombustibleID: number }
export interface Edicion { EdicionID: number; Revista: string; Fecha: string; Paginas: string | null }
export interface Precio { ModeloID: number; EdicionID: number; Precio: number }
export interface Matriculacion { MatriculacionID: number; Fecha: string; Matricula: string; Modelo: string | null; MarcaID: string | null }
export interface Descatalogado { DescatalogadoID: number; Nombre: string; Combustible: string | null; Generaciones: number | null; AnioInicio: number | null; AnioFin: number | null; MarcaID: string }

const marcas: Marca[] = carBrands.map((b) => ({ MarcaID: b.id, Nombre: b.name, Descripcion: b.description || null }));
const perClau = new Map(marcas.map((m) => [clau(m.Nombre), m.MarcaID]));
const marcaId = (nom: string) => perClau.get(ALIES[clau(nom)] ?? clau(nom)) ?? null;

const raw = modelosJson as ModeloRaw[];
// Una marca de la revista que no és a la web també entra (amb el seu slug).
for (const r of raw) {
  if (!marcaId(r.marca)) {
    const m = { MarcaID: slug(r.marca), Nombre: r.marca, Descripcion: null };
    marcas.push(m);
    perClau.set(clau(r.marca), m.MarcaID);
  }
}

const combustibles: Combustible[] = [...new Set(raw.map((r) => r.combustible || "Sense especificar"))]
  .sort((a, b) => a.localeCompare(b, "ca"))
  .map((Nombre, i) => ({ CombustibleID: i + 1, Nombre, Grupo: grupo(Nombre) }));
const combId = new Map(combustibles.map((c) => [c.Nombre, c.CombustibleID]));

export const EDICIONES: Edicion[] = [
  { EdicionID: 1, Revista: "Coches TV", Fecha: "2026-03-01", Paginas: null },
  { EdicionID: 2, Revista: "Coches TV", Fecha: "2026-07-01", Paginas: "91–97" },
];

const modelos: Modelo[] = [];
const precios: Precio[] = [];
raw.forEach((r, i) => {
  const ModeloID = i + 1;
  modelos.push({
    ModeloID,
    Nombre: r.model,
    Potencia: r.potencia,
    Longitud: r.longitud,
    Anchura: r.amplada,
    Altura: r.alcada,
    Maletero: r.maleter,
    MarcaID: marcaId(r.marca)!,
    CombustibleID: combId.get(r.combustible || "Sense especificar")!,
  });
  if (r.preu_marc) precios.push({ ModeloID, EdicionID: 1, Precio: r.preu_marc });
  if (r.preu_juliol) precios.push({ ModeloID, EdicionID: 2, Precio: r.preu_juliol });
});

const matriculaciones: Matriculacion[] = (matriculasJson as MatriculaRaw[]).map((m, i) => {
  const [dd, mm, yy] = m.date.split("/");
  return { MatriculacionID: i + 1, Fecha: `${yy}-${mm}-${dd}`, Matricula: m.plate, Modelo: m.model || null, MarcaID: marcaId(m.brand) };
});

/** "2000–2009" → [2000, 2009]; "2019" → [2019, null]; la resta → [null, null]. */
function anys(s: string | undefined): [number | null, number | null] {
  const m = (s ?? "").match(/(\d{4})\s*[–-]\s*(\d{4})?/) ?? (s ?? "").match(/^(\d{4})$/);
  return m ? [Number(m[1]), m[2] ? Number(m[2]) : null] : [null, null];
}

const descatalogados: Descatalogado[] = carBrands.flatMap((b) =>
  (b.discontinuedModels ?? []).map((m) => {
    const [AnioInicio, AnioFin] = anys(m.years);
    const gen = Number((m.generations ?? "").match(/\d+/)?.[0]);
    return { DescatalogadoID: 0, Nombre: m.name, Combustible: m.fuel || null, Generaciones: gen || null, AnioInicio, AnioFin, MarcaID: b.id };
  }),
).map((d, i) => ({ ...d, DescatalogadoID: i + 1 }));

export const DADES = {
  Marcas: marcas,
  Combustibles: combustibles,
  Modelos: modelos,
  Ediciones: EDICIONES,
  Precios: precios,
  Matriculaciones: matriculaciones,
  Descatalogados: descatalogados,
} satisfies Record<string, object[]>;

/** Les dades tal com arriben, per ensenyar què canvia en carregar-les. */
export const CRU = { modelos: raw, matriculas: matriculasJson as MatriculaRaw[] };
